
import { Link } from 'react-router-dom';
import { Check, TriangleAlert } from 'lucide-react';
import type { TaskAssigneeRef } from '@/shared/api';
import { Avatar, Tooltip } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { layoutHours } from './hours-breakdown';
import styles from './HoursBreakdown.module.css';

export interface HoursRow {
  id: string;
  label: string;
  title: string;
  estimate: number | null;
  spent: number;
  done: boolean;
  statusLabel?: string;
  statusColor?: string | null;
  href?: string;
  meta?: string;
  assignees?: TaskAssigneeRef[];
}

interface Props {
  plan: number | null;
  planLabel: string;
  totalLabel: string;
  overLabel: string;
  rows: HoursRow[];
  hideRows?: boolean;
  footer?: React.ReactNode;
  className?: string;
}

function hours(min: number): string {
  if (min > 0 && min < 60) return `${Math.round(min)} мин`;
  const value = Math.round((min / 60) * 10) / 10;
  return `${String(value).replace('.', ',')} ч`;
}

export function HoursBreakdown({
  plan,
  planLabel,
  totalLabel,
  overLabel,
  rows,
  hideRows,
  footer,
  className,
}: Props) {
  const layout = layoutHours(
    plan,
    rows.map((r) => ({ id: r.id, estimate: r.estimate, spent: r.spent, done: r.done })),
  );
  const byId = new Map(rows.map((r) => [r.id, r]));
  const hasBar = layout.segments.length > 0 || layout.plan != null;

  return (
    <div className={cn(styles.root, className)}>
      <p className={styles.summary}>
        {layout.plan != null && (
          <span>
            {planLabel} <strong className={styles.value}>{hours(layout.plan)}</strong>
          </span>
        )}
        <span>
          {totalLabel} <strong className={styles.value}>{hours(layout.total)}</strong>
        </span>
        <span>
          потрачено <strong className={styles.value}>{hours(layout.spent)}</strong>
        </span>
        {layout.over > 0 && (
          <span className={styles.over}>
            <TriangleAlert size={13} aria-hidden />+{hours(layout.over)} {overLabel}
          </span>
        )}
        {layout.uncut > 0 && <span className={styles.uncut}>не нарезано {hours(layout.uncut)}</span>}
        {layout.withoutEstimate > 0 && (
          <span className={styles.uncut}>без оценки: {layout.withoutEstimate}</span>
        )}
      </p>

      {hasBar && (
        <div
          className={styles.bar}
          role="img"
          aria-label={`${totalLabel} ${hours(layout.total)}${
            layout.plan != null ? ` при плане ${hours(layout.plan)}` : ''
          }, потрачено ${hours(layout.spent)}`}
        >
          <div className={styles.track}>
            {layout.segments.map((seg) => {
              const row = byId.get(seg.id);
              return (
                <Tooltip
                  key={seg.id}
                  content={
                    row
                      ? `${row.label} · ${row.title} — ${hours(row.spent)} из ${hours(row.estimate ?? 0)}${
                          row.done ? ', сделано' : ''
                        }`
                      : undefined
                  }
                >
                  <span
                    className={cn(styles.segment, seg.done && styles.segmentDone)}
                    style={{ flexBasis: `${seg.width * 100}%` }}
                  >
                    <span className={styles.segmentFill} style={{ width: `${seg.filled * 100}%` }} />
                  </span>
                </Tooltip>
              );
            })}
            {layout.uncut > 0 && <span className={styles.uncutRest} aria-hidden />}
          </div>
          {layout.planAt != null && layout.over > 0 && (
            <span
              className={styles.overZone}
              style={{ left: `${layout.planAt * 100}%` }}
              aria-hidden
            />
          )}
          {layout.planAt != null && (
            <span className={styles.planMark} style={{ left: `${layout.planAt * 100}%` }} aria-hidden />
          )}
        </div>
      )}

      {!hideRows && rows.length > 0 && (
        <ul className={styles.rows}>
          {rows.map((row) => {
            const ratio = row.estimate ? Math.min(1, row.spent / row.estimate) : 0;
            const overrun = row.estimate != null && row.spent > row.estimate;
            return (
              <li key={row.id} className={styles.row}>
                <span
                  className={cn(styles.dot, styles[`dot${row.statusColor ?? 'neutral'}`])}
                  aria-hidden
                />
                <span className={styles.key}>{row.label}</span>
                <span className={styles.titleCell}>
                  {row.href ? (
                    <Link to={row.href} className={cn(styles.title, row.done && styles.titleDone)} title={row.title}>
                      {row.title}
                    </Link>
                  ) : (
                    <span className={cn(styles.title, row.done && styles.titleDone)} title={row.title}>
                      {row.title}
                    </span>
                  )}
                  {(row.statusLabel || row.meta) && (
                    <span className={styles.rowMeta}>
                      {row.done && <Check size={12} aria-hidden />}
                      {[row.statusLabel, row.meta].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </span>
                <span className={styles.mini} aria-hidden>
                  <span
                    className={cn(styles.miniFill, row.done && styles.miniDone, overrun && styles.miniOver)}
                    style={{ width: `${(row.done ? 1 : ratio) * 100}%` }}
                  />
                </span>
                <span className={cn(styles.hours, overrun && styles.hoursOver)}>
                  {row.estimate != null ? `${hours(row.spent)} / ${hours(row.estimate)}` : 'без оценки'}
                </span>
                <span className={styles.people}>
                  {(row.assignees ?? []).slice(0, 2).map((a) => (
                    <Tooltip key={a.id} content={a.displayName}>
                      <Avatar className={styles.avatar} name={a.displayName} seed={a.id} src={a.avatarUrl} />
                    </Tooltip>
                  ))}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {footer}
    </div>
  );
}
